import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { SxProps, Theme } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TwEmptyMiles from '../../../components/TwEmptyMiles/TwEmptyMiles';
import TwLoadedMiles from '../../../components/TwLoadedMiles/TwLoadedMiles';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../../../components/TwAutocomplete/TwAutocomplete';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import useAuth from '../../../providers/Auth/Auth.provider';
import { Equipment, EquipmentType } from '../../../redux/api/equipment/equipment.model';
import CreateDriver from '../../Drivers/CreateDriver/CreateDriver';
import CreateEquipment from '../../Equipment/CreateEquipment/CreateEquipment';
import CreateDispatcher from '../../Dispatchers/CreateDispatcher/CreateDispatcher';
import EditDriver from '../../Drivers/EditDriver/EditDriver';
import EditEquipment from '../../Equipment/EditEquipment/EditEquipment';
import EditDispatcher from '../../Dispatchers/EditDispatcher/EditDispatcher';
import TwTooltip from '../../../components/TwTooltip/TwTooltip';

import {
  MIN_STOP_COUNT,
  addCarrier,
  addDriver,
  addEquipment,
  addLoadDriver,
  deleteDriver,
  updateGrossPayEstimateData,
  updateSelectedCarrier,
  updateSelectedDispatcher,
  updateSelectedDriver,
  updateSelectedTrailer,
  updateSelectedTruck,
  updateOtherPayItemsData,
  updateTripInstructions,
  updateEstimatedMileageSegment,
  updateAutomaticMileage,
  selectIsManualLoadedMilesPresetByTripId,
  SliceStop,
  updateDriverPayConfirmationModal,
  addOwnerOperator,
  updateSelectedOwnerOperator
} from '../../../redux/features/orders/ordersSlice';
import { CarrierResponse } from '../../../redux/api/carriers/carriers.model';
import { DispatcherResponse } from '../../../redux/api/dispatchers/dispatcher.model';
import TwCompaniesModal from '../../Companies/CompaniesModal/TwCompaniesModal';
import { CompaniesEntities } from '../../Companies/Companies';
import TwEditCompaniesModal from '../../Companies/EditCompaniesModal/TwEditCompaniesModal';
import { CreateDriverData, Driver } from '../../../redux/api/driver/driver.model';
import TwDriverPayCalculator from '../../../components/TwDriverPayCalc/TwDriverPayCalculator';
import { FormikErrors, useFormikContext } from 'formik';
import LoadsService from '../../../providers/Loads/Loads.service';
import StringUtils from '../../../utils/stringUtils';
import { useGetCarriersQuery } from '../../../redux/api/carriers/carrierApi';
import { useGetDriversQuery } from '../../../redux/api/driver/driverApi';
import { useSearchEquipmentsQuery } from '../../../redux/api/equipment/equipmentApi';
import { useGetDispatchersQuery } from '../../../redux/api/dispatchers/dispatcherApi';
import { setAnchorId } from '../../../redux/features/navigation/navigationSlice';
import useScrollToView from '../../../hooks/useScrollToView';
import './Dispatch.scss';
import {
  TripDriverInfo,
  TripDriverOtherPay,
  TripDriverOverrideData
} from '../../../redux/api/trips/trips.model';
import {
  addTripCarrier,
  addTripDriver,
  addTripEquipment,
  addTripOwnerOperator,
  addTripSecondDriver,
  deleteTripDriver,
  updateTripDriverPayData,
  updateTripLoadedMiles,
  updateTripLtlDriverPayConfirmationModal,
  updateTripNotes,
  updateTripOtherPayItemsData,
  updateTripSelectedCarrier,
  updateTripSelectedDispatcher,
  updateTripSelectedDriver,
  updateTripSelectedOwnerOperator,
  updateTripSelectedTrailer,
  updateTripSelectedTruck
} from '../../../redux/features/trip/tripSlice';
import { EntityType } from '../../../redux/Global.model';
import { PROVIDER, USER_STATUS } from '../../../providers/User/User.model';
import { useGetTenantRoutingSettingsQuery } from '../../../redux/api/tenant/tenantApi';
import useCheckDriverPay from '../../../hooks/useCheckDriverPay';
import TwEquipmentSelector from '../../../components/TwEquipmentSelector/TwEquipmentSelector';
import { OwnerOperator } from '../../../redux/api/ownerOperators/ownerOperators.model';
import { useGetOwnerOperatorsQuery } from '../../../redux/api/ownerOperators/ownerOperatorsApi';
import TwExpandableTextarea from '../../../components/TwExpandableTextArea/TwExpandableTextArea';

export interface ContactFormDriver extends Partial<TripDriverInfo> {
  autocompleteDriver: TwAutocompleteOption | null | '';
}
interface ContactsFormValues {
  drivers: ContactFormDriver[];
  truck: TwAutocompleteOption | null;
  trailer: TwAutocompleteOption | null;
  carrier: TwAutocompleteOption | null;
  dispatcher: TwAutocompleteOption | null;
  ownerOperator: TwAutocompleteOption | null;
}

interface DispatchInfoProps {
  isImport?: boolean;
  fullScreenImport?: boolean;
  dispatchContainerSx?: SxProps;
  loading?: boolean;
  /*
   * Will determine if the dispatch info is for a trip or an order slice
   */
  entity: EntityType;
}
const MAXIMUM_DRIVERS_ALLOWED = 2;

const DispatchInfo = forwardRef<HTMLDivElement, DispatchInfoProps>(
  ({ isImport, fullScreenImport, dispatchContainerSx, loading, entity }, ref) => {
    const {
      data: { userData },
      getTenantSetting
    } = useAuth();
    const loadState = useAppSelector((state) => state.orders);
    const {
      id: tripId,
      ui_id: tripUiId,
      provider_trip_stops,
      selected_drivers: ltlTripDrivers,
      loaded_miles: tripEstimatedMileage,
      empty_miles: tripEmptyMiles,
      is_empty_miles_auto: tripIsEmptyMilesAuto,
      is_loaded_miles_auto: tripLoadedMilesAuto,
      selected_truck: tripSelectedTruck,
      selected_trailer: tripSelectedTrailer,
      selected_carrier: tripSelectedCarrier,
      selected_owner_operator: tripSelectedOwnerOperator,
      instructions: tripLtlInstructions,
      selected_dispatcher: tripSelectedDispatcher,
      empty_mileage_source: tripEmptyMileageSource,
      loaded_mileage_source: tripLoadedMileageSource
    } = useAppSelector((state) => state.trip);
    const dispatch = useAppDispatch();
    const [scrollToView] = useScrollToView();
    const enableLtl = userData?.tenant?.ltl_enabled || false;
    const segments = loadState.trips;

    const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
    const anyDriverPayExists = useCheckDriverPay({ entity });
    const {
      enableDriverGrossPayEstimate,
      enableTeamDrivers,
      enablePcMiler,
      enableHideGoogleMapsAndRoutingCalculations
    } = useFeatureFlags();
    const anchorId = useAppSelector((state) => state.navigation.anchorId);
    const isEditLoadOpen = useAppSelector((state) => state.navigation.isEditLoadOpen) && !enableLtl;
    const isEditTripOpen = useAppSelector((state) => state.navigation.isEditTripOpen) && enableLtl;
    const isCreateTripOpen = useAppSelector((state) => state.navigation.isCreateTripOpen);
    const isCreateLoadOpen = useAppSelector((state) => state.navigation.isCreateLoadOpen);
    const driverRef = useRef<HTMLDivElement | null>(null);
    const [visibleRef, setVisibleRef] = useState<boolean>(false);
    const [createDriverDialogOpen, setCreateDriverDialogOpen] = useState(false);
    const [createEquipmentDialogOpen, setCreateEquipmentDialogOpen] = useState(false);
    const [createEquipmentType, setCreateEquipmentType] = useState<EquipmentType | undefined>(
      undefined
    );
    const [createCompanyDialogOpen, setCreateCompanyDialogOpen] = useState({
      entity: CompaniesEntities.carrier,
      open: false
    });
    const [ownerOperators, setOwnerOperators] = useState<TwAutocompleteOption[]>([]);
    const [carriers, setCarriers] = useState<TwAutocompleteOption[]>([]);
    const [dispatchers, setDispatchers] = useState<TwAutocompleteOption[]>([]);
    const [drivers, setDrivers] = useState<TwAutocompleteOption[]>([]);
    const [trailers, setTrailers] = useState<TwAutocompleteOption[]>([]);
    const [trucks, setTrucks] = useState<TwAutocompleteOption[]>([]);
    const [createDispatcherDialogOpen, setCreateDispatcherDialogOpen] = useState(false);
    const [currentDriverIndex, setCurrentDriverIndex] = useState<number>(0);
    const [currentDrivers, setCurrentDrivers] = useState<ContactFormDriver[]>([]);
    const [specialInstructions, setSpecialInstructions] = useState<string | null | undefined>();
    const [initialSpecialInstructions, setInitialSpecialInstructions] = useState<
      string | null | undefined
    >();
    const [firstRender, setFirstRender] = useState<boolean>(true);
    /* Edit dialogs states */
    const [selectedDriverId, setSelectedDriverId] = useState<string>('');
    const [editDriverOpen, setEditDriverOpen] = useState<boolean>(false);

    const [selectedEquipmentId, setSelectedEquipmentId] = useState<string>('');
    const [editEquipmentOpen, setEditEquipmentOpen] = useState<boolean>(false);

    const [selectedCarrierId, setSelectedCarrierId] = useState<string>('');
    const [editCarrierOpen, setEditCarrierOpen] = useState<boolean>(false);

    const [selectedDispatcherId, setSelectedDispatcherId] = useState<string>('');
    const [editDispatcherOpen, setEditDispatcherOpen] = useState<boolean>(false);
    const [selectedTruck, setSelectedTruck] = useState<TwAutocompleteOption | null | undefined>(
      null
    );
    const [selectedTrailer, setSelectedTrailer] = useState<TwAutocompleteOption | null | undefined>(
      null
    );
    const [selectedCarrier, setSelectedCarrier] = useState<TwAutocompleteOption | null | undefined>(
      null
    );
    const [selectedOwnerOperator, setSelectedOwnerOperator] = useState<
      TwAutocompleteOption | null | undefined
    >(null);
    const [selectedDispatcher, setSelectedDispatcher] = useState<
      TwAutocompleteOption | null | undefined
    >(null);
    const [renderCount, setRenderCount] = useState<number>(0);

    const ownerOperatorEnabled = getTenantSetting('owner-operators');
    const carriersEnabled = getTenantSetting('multiple-carriers');
    const dispatcherEnable = getTenantSetting('multiple-dispatchers');
    const advancedDriverFieldsEnable = getTenantSetting('advanced-driver-fields');
    const isEmptyMilesEnabled = getTenantSetting('empty-miles');
    const isAutoCalculateMileageDisabled = getTenantSetting('disable-auto-calculate-mileage');

    const { values, setFieldValue } = useFormikContext<ContactsFormValues>();

    const isManualLoadedMilesPresentForTrip = useAppSelector((state) =>
      selectIsManualLoadedMilesPresetByTripId(state, segments[0].ui_id)
    );

    const { data: carriersData = { content: [], total: 0 } } = useGetCarriersQuery({});
    const { data: driversData = { content: [], total: 0 } } = useGetDriversQuery({});
    const { data: equipmentData = { equipments: [], total: 0 } } = useSearchEquipmentsQuery({
      params: { deleted: isEditLoadOpen ? true : false }
    });
    const { data: dispatchersData = { content: [], total: 0 } } = useGetDispatchersQuery({});
    const { data: routingSettings } = useGetTenantRoutingSettingsQuery(null);
    const { data: ownerOperatorsList = { content: [], total: 0 } } = useGetOwnerOperatorsQuery(
      { status: USER_STATUS.ACTIVE },
      {
        skip: !ownerOperatorEnabled
      }
    );
    // #region Functions
    const assignLoggedDispatcher = (): void => {
      const loggedDispatcher = dispatchers.find(
        (dispatcher) => (dispatcher.value as DispatcherResponse).twuser_id === userData?.id
      );
      if (loggedDispatcher) {
        setSelectedDispatcher({
          ...selectedDispatcher,
          id: loggedDispatcher.id,
          label: `${(loggedDispatcher.value as DispatcherResponse).first_name} ${(loggedDispatcher.value as DispatcherResponse).last_name}`,
          value: loggedDispatcher.id
        });

        if (enableLtl && entity === EntityType.TRIP) {
          dispatch(updateTripSelectedDispatcher(loggedDispatcher.id));
        } else {
          dispatch(updateSelectedDispatcher(loggedDispatcher.id));
        }
      }
    };

    const driversForOption1 = React.useMemo(() => {
      if (currentDrivers.length > 1) {
        return drivers.filter((driver) => driver.id !== currentDrivers[1].id) || [];
      }

      return drivers || [];
    }, [drivers, currentDrivers]);

    const driversForOption2 = React.useMemo(() => {
      if (currentDrivers.length > 0) {
        return drivers.filter((driver) => driver.id !== currentDrivers[0].id) || [];
      }

      return drivers || [];
    }, [drivers, currentDrivers]);

    const handleEditDriverClose = (): void => {
      setEditDriverOpen(false);
      setSelectedDriverId('');
    };

    const handleEditEquipmentClose = (): void => {
      setEditEquipmentOpen(false);
      setSelectedEquipmentId('');
    };

    const handleEditDispatcherClose = (): void => {
      setEditDispatcherOpen(false);
      setSelectedDispatcherId('');
    };

    /**
     * Helper function to maybe set truck, carrier, and trailer from driver data  when a driver is created or
     * edited only if load is being created or if an existing load and that object is not already set
     *
     * @param truckResponse
     * @param carrierResponse
     * @param trailerResponse
     * @param ownerOperatorResponse
     * */
    const maybeUpdateDriverTruckCarrierByDriver = (
      truckResponse?: Equipment,
      carrierResponse?: CarrierResponse,
      trailerResponse?: Equipment,
      ownerOperatorResponse?: OwnerOperator
    ): {
      truckId: string | undefined;
      carrierId: string | undefined;
      trailerId: string | undefined;
      ownerOperatorId: string | undefined;
    } => {
      const isNew = enableLtl && entity === EntityType.TRIP ? !tripId : !loadState.order_id;
      let truckId: string | undefined = undefined;
      let carrierId: string | undefined = undefined;
      let trailerId: string | undefined = undefined;
      let ownerOperatorId: string | undefined = undefined;
      // only need to set if it's a new load or trip if no truck is selected
      if ((isNew || !selectedTruck) && truckResponse) {
        const newTruck = trucks.find((e) => e.id === truckResponse.id);
        truckId = newTruck?.id;
        setFieldValue('truck', newTruck);
        if (enableLtl && entity === EntityType.TRIP) {
          dispatch(
            updateTripSelectedTruck({
              value: truckId,
              preserveEmptyMiles: isAutoCalculateMileageDisabled
            })
          );
        } else {
          dispatch(
            updateSelectedTruck({
              segmentOrder: 1,
              value: truckId,
              preserveEmptyMiles: isAutoCalculateMileageDisabled
            })
          );
        }
      }

      // only need to set if it's a new load or if no trailer is selected
      if ((isNew || !selectedTrailer) && trailerResponse) {
        const newTrailer = trailers.find((e) => e.id === trailerResponse.id);
        trailerId = newTrailer?.id;
        setFieldValue('trailer', newTrailer);
        if (enableLtl && entity === EntityType.TRIP) {
          dispatch(updateTripSelectedTrailer({ value: trailerId }));
        } else {
          dispatch(
            updateSelectedTrailer({
              segmentOrder: 1,
              value: trailerId
            })
          );
        }
      }

      // only need to set if it's a new load or if no carrier is selected
      if ((isNew || !selectedCarrier) && carrierResponse && carriersEnabled) {
        const newCarrier = carriers.find((c) => c.id === carrierResponse.id);

        carrierId = newCarrier?.id;
        setFieldValue('carrier', newCarrier);

        if (enableLtl && entity === EntityType.TRIP) {
          dispatch(updateTripSelectedCarrier({ value: carrierId }));
        } else {
          dispatch(
            updateSelectedCarrier({
              segmentOrder: 1,
              value: carrierId
            })
          );
        }
      }

      if (ownerOperatorResponse && ownerOperatorEnabled) {
        ownerOperatorId = ownerOperatorResponse.id;
        const autocompleteOwnerOperator = ownerOperators.find(
          (o) => o.id === ownerOperatorResponse.id
        );
        setFieldValue('ownerOperator', autocompleteOwnerOperator);
        if (enableLtl && entity === EntityType.TRIP) {
          dispatch(updateTripSelectedOwnerOperator({ value: ownerOperatorId }));
        } else {
          dispatch(updateSelectedOwnerOperator({ segmentOrder: 1, value: ownerOperatorId }));
        }
      }

      return { truckId, carrierId, trailerId, ownerOperatorId };
    };

    const onDriverCreated = (
      newDriver: Driver,
      truckResponse?: Equipment,
      carrierResponse?: CarrierResponse,
      trailerResponse?: Equipment,
      ownerOperatorResponse?: OwnerOperator
    ): void => {
      const newAutocompleteOption: TwAutocompleteOption = {
        id: newDriver.id,
        label: `${newDriver.first_name} ${newDriver.last_name}`,
        value: {
          ...newDriver,
          is_primary: currentDriverIndex === 0
        }
      };

      const currentDrivers = [...values.drivers];
      currentDrivers[currentDriverIndex] = {
        ...currentDrivers[currentDriverIndex],
        id: newDriver.id,
        driver_pay_override: undefined,
        driver_pay_override_data: undefined,
        trip_driver_pay_other_payment: [],
        autocompleteDriver: newAutocompleteOption,
        is_primary: currentDriverIndex === 0
      };

      setFieldValue('drivers', currentDrivers);

      const { truckId, carrierId, trailerId, ownerOperatorId } =
        maybeUpdateDriverTruckCarrierByDriver(
          truckResponse,
          carrierResponse,
          trailerResponse,
          ownerOperatorResponse
        );

      if (enableLtl && entity === EntityType.TRIP) {
        dispatch(
          addTripDriver({
            params: {
              driver: newDriver,
              index: currentDriverIndex,
              selectedTruck: truckId,
              selectedCarrier: carrierId,
              selectedTrailer: trailerId,
              selectedOwnerOperator: ownerOperatorId
            }
          })
        );
      } else {
        dispatch(
          addDriver({
            params: {
              driver: newDriver,
              index: currentDriverIndex,
              selectedTruck: truckId,
              selectedCarrier: carrierId,
              selectedTrailer: trailerId,
              selectedOwnerOperator: ownerOperatorId
            },
            segmentOrder: 1
          })
        );
      }
    };

    const onDriverEditSuccess = async (
      driverId: string,
      driverData?: CreateDriverData,
      selectedTruck?: Equipment,
      selectedCarrier?: CarrierResponse,
      selectedTrailer?: Equipment,
      selectedOwnerOperator?: OwnerOperator,
      clearDriverPay?: boolean
    ): Promise<void> => {
      const driver = drivers?.find((d) => d.id === driverId);
      const currentDrivers = [...values.drivers];
      const index = currentDrivers.findIndex((d) => d.id === driver?.id);

      if (index !== -1 && driver) {
        currentDrivers[index] = {
          ...currentDrivers[index],
          autocompleteDriver: driver
        };

        setFieldValue('drivers', currentDrivers);

        const { truckId, carrierId, trailerId, ownerOperatorId } =
          maybeUpdateDriverTruckCarrierByDriver(
            selectedTruck,
            selectedCarrier,
            selectedTrailer,
            selectedOwnerOperator
          );

        const updatedDriver = {
          driver: {
            ...currentDrivers[index]
          },
          index,
          segmentOrder: 1,
          selectedCarrier: carrierId,
          selectedTruck: truckId,
          selectedTrailer: trailerId,
          selectedOwnerOperator: ownerOperatorId
        };

        if (clearDriverPay) {
          updatedDriver.driver.driver_pay_override = undefined;
          updatedDriver.driver.driver_pay_override_data = undefined;
          updatedDriver.driver.trip_driver_pay_other_payment = [];
        }

        if (enableLtl && entity === EntityType.TRIP) {
          dispatch(updateTripSelectedDriver(updatedDriver));
        } else {
          dispatch(updateSelectedDriver(updatedDriver));
        }
      }
    };

    const onDriverSelected = ({
      driver,
      index,
      autocompleteDriver,
      truckId,
      carrierId,
      trailerId,
      ownerOperatorId
    }: {
      driver: TripDriverInfo;
      index: number;
      autocompleteDriver: TwAutocompleteOption;
      truckId?: string;
      carrierId?: string;
      trailerId?: string;
      ownerOperatorId?: string;
    }): void => {
      const currentDrivers = [...values.drivers];

      const newCurrentDriver: ContactFormDriver = {
        id: autocompleteDriver.id,
        autocompleteDriver: autocompleteDriver,
        send_driver_link: false,
        is_primary: index === 0
      };

      newCurrentDriver.driver_pay_override = undefined;
      newCurrentDriver.driver_pay_override_data = undefined;
      newCurrentDriver.trip_driver_pay_other_payment = [];

      if (enableLtl && entity === EntityType.TRIP) {
        dispatch(
          updateTripSelectedDriver({
            driver: newCurrentDriver,
            index: index,
            selectedCarrier: carrierId,
            selectedTruck: truckId,
            selectedTrailer: trailerId,
            selectedOwnerOperator: ownerOperatorId
          })
        );
      } else {
        dispatch(
          updateSelectedDriver({
            driver: newCurrentDriver,
            index: index,
            segmentOrder: 1,
            selectedCarrier: carrierId,
            selectedTruck: truckId,
            selectedTrailer: trailerId,
            selectedOwnerOperator: ownerOperatorId
          })
        );
      }

      currentDrivers[index] = newCurrentDriver;

      setCurrentDrivers(currentDrivers);
      setFieldValue('drivers', currentDrivers);
    };

    const onEquipmentCreated = (newEquipment: Equipment): void => {
      if (enableLtl && entity === EntityType.TRIP) {
        dispatch(
          addTripEquipment({
            equipment: {
              id: newEquipment.id,
              type: newEquipment.type
            }
          })
        );
      } else {
        dispatch(
          addEquipment({
            equipment: {
              id: newEquipment.id,
              type: newEquipment.type
            },
            segmentOrder: 1
          })
        );
      }

      const newAutocompleteOption: TwAutocompleteOption = {
        id: newEquipment.id,
        label: newEquipment.name,
        value: newEquipment
      };

      if (newEquipment.type === 'TRUCK') {
        setFieldValue('truck', newAutocompleteOption);

        if (enableLtl && entity === EntityType.TRIP) {
          dispatch(
            updateTripSelectedTruck({
              value: newEquipment.id,
              preserveEmptyMiles: isAutoCalculateMileageDisabled
            })
          );
        } else {
          dispatch(
            updateSelectedTruck({
              segmentOrder: 1,
              value: newEquipment.id,
              preserveEmptyMiles: isAutoCalculateMileageDisabled
            })
          );
        }
      }

      if (newEquipment.type === 'TRAILER') {
        setFieldValue('trailer', newAutocompleteOption);
        if (enableLtl && entity === EntityType.TRIP) {
          dispatch(updateTripSelectedTrailer({ value: newEquipment.id }));
        } else {
          dispatch(
            updateSelectedTrailer({
              segmentOrder: 1,
              value: newEquipment.id
            })
          );
        }
      }
    };

    const onCompanyCreated = (newCompany: CarrierResponse | OwnerOperator): void => {
      if (createCompanyDialogOpen.entity === CompaniesEntities.carrier) {
        onCarrierCreated(newCompany as CarrierResponse);
      } else {
        onOwnerOperatorCreated(newCompany as OwnerOperator);
      }
    };

    const onCarrierCreated = (newCarrier: CarrierResponse): void => {
      if (enableLtl && entity === EntityType.TRIP) {
        dispatch(
          addTripCarrier({
            carrierId: newCarrier.id
          })
        );
      } else {
        dispatch(
          addCarrier({
            carrierId: newCarrier.id,
            segmentOrder: 1
          })
        );
      }

      const newAutocompleteOption: TwAutocompleteOption = {
        id: newCarrier.id,
        label: newCarrier.name,
        value: newCarrier
      };
      setFieldValue('carrier', newAutocompleteOption);
    };

    const onOwnerOperatorCreated = (newOwnerOperator: OwnerOperator): void => {
      if (enableLtl && entity === EntityType.TRIP) {
        dispatch(addTripOwnerOperator({ ownerOperatorId: newOwnerOperator.id }));
      } else {
        dispatch(addOwnerOperator({ ownerOperatorId: newOwnerOperator.id, segmentOrder: 1 }));
      }

      const newAutocompleteOption: TwAutocompleteOption = {
        id: newOwnerOperator.id,
        label: newOwnerOperator.company.name,
        value: newOwnerOperator.id
      };
      setFieldValue('ownerOperator', newAutocompleteOption);
    };

    const onDispatcherCreated = (newDispatcher: DispatcherResponse): void => {
      if (enableLtl && entity === EntityType.TRIP) {
        dispatch(updateTripSelectedDispatcher(newDispatcher.id));
      } else {
        dispatch(updateSelectedDispatcher(newDispatcher.id));
      }

      const newAutocompleteOption: TwAutocompleteOption = {
        id: newDispatcher.id,
        label: `${newDispatcher.first_name} ${newDispatcher.last_name}`,
        value: newDispatcher.id
      };
      setFieldValue('dispatcher', newAutocompleteOption);
    };

    const getEditTooltipText = (inputName: string): string => {
      return `Editing selected ${inputName} will impact all loads where the ${inputName} is used`;
    };

    const onKeyDownHandler = (e: React.KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Enter') {
        if (e.currentTarget.outerHTML.includes('driver')) {
          setCreateDriverDialogOpen(true);
        }
        if (e.currentTarget.outerHTML.includes('carrier')) {
          setCreateCompanyDialogOpen({
            entity: CompaniesEntities.carrier,
            open: true
          });
        }
        if (e.currentTarget.outerHTML.includes('owner')) {
          setCreateCompanyDialogOpen({
            entity: CompaniesEntities.ownerOperator,
            open: true
          });
        }
        if (e.currentTarget.outerHTML.includes('dispatcher')) {
          setCreateDispatcherDialogOpen(true);
        }
        if (e.currentTarget.outerHTML.includes('trailer')) {
          setCreateEquipmentType(EquipmentType.trailer);
          setCreateEquipmentDialogOpen(true);
        }
        if (e.currentTarget.outerHTML.includes('truck')) {
          setCreateEquipmentType(EquipmentType.truck);
          setCreateEquipmentDialogOpen(true);
        }
      }
    };

    const handleEditCompaniesClose = (): void => {
      setEditCarrierOpen(false);

      setSelectedCarrierId('');
    };

    const openEditDialog = (
      values: ContactsFormValues,
      inputName: string,
      index?: number
    ): void => {
      switch (inputName) {
        case 'driver':
          if (index !== undefined && !!values.drivers[index].id) {
            setSelectedDriverId(values.drivers[index].id as string);
            setEditDriverOpen(true);
          }
          break;
        case 'carrier':
          if (!!values.carrier) {
            setSelectedCarrierId(values.carrier.id);
            setEditCarrierOpen(true);
          } else if (!!selectedCarrier) {
            setSelectedCarrierId(selectedCarrier.id);
            setEditCarrierOpen(true);
          }
          break;
        case 'dispatcher':
          if (!!values.dispatcher) {
            setSelectedDispatcherId(values.dispatcher.id);
            setEditDispatcherOpen(true);
          } else if (!!selectedDispatcher) {
            setSelectedDispatcherId(selectedDispatcher.id);
            setEditDispatcherOpen(true);
          }
          break;
        case 'truck':
          if (!!values.truck) {
            setSelectedEquipmentId(values.truck.id);
            setEditEquipmentOpen(true);
          } else if (!!selectedTruck) {
            setSelectedEquipmentId(selectedTruck.id);
            setEditEquipmentOpen(true);
          }
          break;
        case 'trailer':
          if (!!values.trailer) {
            setSelectedEquipmentId(values.trailer.id);
            setEditEquipmentOpen(true);
          } else if (!!selectedTrailer) {
            setSelectedEquipmentId(selectedTrailer.id);
            setEditEquipmentOpen(true);
          }
          break;
      }
    };

    const addAnotherDriver = (): void => {
      if (enableLtl && entity === EntityType.TRIP) {
        if (anyDriverPayExists) {
          dispatch(
            updateTripLtlDriverPayConfirmationModal({
              open: true,
              action: () => dispatch(addTripSecondDriver())
            })
          );
        } else {
          dispatch(addTripSecondDriver());
        }
      } else {
        if (anyDriverPayExists) {
          dispatch(
            updateDriverPayConfirmationModal({
              open: true,
              action: () =>
                dispatch(
                  addLoadDriver({
                    segmentOrder: 1,
                    callback: () => null
                  })
                )
            })
          );
        } else {
          dispatch(
            addLoadDriver({
              segmentOrder: 1,
              callback: () => null
            })
          );
        }
      }
    };

    const handleDriverChange = (
      _: any,
      newValue: TwAutocompleteOption | null,
      index: number,
      driver: any
    ): Promise<void> | Promise<FormikErrors<ContactsFormValues>> | undefined => {
      let truckId: string | undefined = undefined;
      let carrierId: string | undefined = undefined;
      let trailerId: string | undefined = undefined;
      let ownerOperatorId: string | undefined = undefined;

      if (newValue === null) {
        if (enableLtl && entity === EntityType.TRIP) {
          dispatch(
            updateTripSelectedDriver({
              driver: {
                id: '',
                driver_pay_override: undefined,
                driver_pay_override_data: undefined,
                trip_driver_pay_other_payment: [],
                is_primary: false,
                autocompleteDriver: null
              },
              index
            })
          );
        } else {
          dispatch(
            updateSelectedDriver({
              driver: {
                id: '',
                driver_pay_override: undefined,
                driver_pay_override_data: undefined,
                trip_driver_pay_other_payment: [],
                is_primary: false,
                autocompleteDriver: null
              },
              index,
              segmentOrder: 1
            })
          );
        }

        const currentDrivers = [...values.drivers];
        currentDrivers[index] = { id: '', autocompleteDriver: null };

        setCurrentDrivers(currentDrivers);
        setFieldValue('drivers', currentDrivers);
      }

      if (newValue?.value) {
        if (index === 0) {
          const newTruck = (newValue.value as Driver).default_truck
            ? trucks.find((e) => e.id === (newValue.value as Driver).default_truck?.id)
            : trucks.find((e) => e.id === (newValue.value as Driver).default_truck_id);

          if (newTruck && !selectedTruck) {
            truckId = newTruck?.id;
            setFieldValue('truck', newTruck || null);

            if (enableLtl && entity === EntityType.TRIP) {
              dispatch(
                updateTripSelectedTruck({
                  value: truckId,
                  preserveEmptyMiles: isAutoCalculateMileageDisabled
                })
              );
            } else {
              dispatch(
                updateSelectedTruck({
                  segmentOrder: 1,
                  value: truckId,
                  preserveEmptyMiles: isAutoCalculateMileageDisabled
                })
              );
            }
          }

          // only want to set the carrier if creating a new load or if no carrier is selected for existing load
          if (carriersEnabled && !selectedCarrier && !values.carrier) {
            const newCarrier = carriers.find(
              (c) => c.id === (newValue.value as Driver).carrier?.id
            );

            carrierId = newCarrier?.id;
            setFieldValue('carrier', newCarrier || null);

            if (enableLtl && entity === EntityType.TRIP) {
              dispatch(updateTripSelectedCarrier({ value: carrierId || '' }));
            } else {
              dispatch(
                updateSelectedCarrier({
                  segmentOrder: 1,
                  value: carrierId || ''
                })
              );
            }
          }

          const newTrailer = (newValue.value as Driver).default_trailer
            ? trailers.find((e) => e.id === (newValue.value as Driver).default_trailer?.id)
            : trailers.find((e) => e.id === (newValue.value as Driver).default_trailer_id);

          if (newTrailer && !selectedTrailer) {
            trailerId = newTrailer?.id;
            setFieldValue('trailer', newTrailer || null);
            if (enableLtl && entity === EntityType.TRIP) {
              dispatch(updateTripSelectedTrailer({ value: trailerId }));
            } else {
              dispatch(
                updateSelectedTrailer({
                  segmentOrder: 1,
                  value: trailerId
                })
              );
            }
          }
        }
        const driverOwnerOperator = (newValue.value as Driver).owner_operator
          ? ownerOperators.find((o) => o.id === (newValue.value as Driver).owner_operator?.id)
          : null;

        if (driverOwnerOperator) {
          ownerOperatorId = driverOwnerOperator.id;
          setFieldValue('ownerOperator', driverOwnerOperator);
        }
        const newDriver = { ...driver } as any;
        delete newDriver.autocompleteDriver;
        onDriverSelected({
          driver: {
            ...(newDriver as TripDriverInfo),
            id: newValue.id,
            send_driver_link: false,
            is_primary: index === 0
          },
          autocompleteDriver: newValue,
          index,
          truckId,
          carrierId,
          trailerId,
          ownerOperatorId
        });

        return undefined;
      }
    };
    // #endregion

    // #region Effects
    useEffect(() => {
      if (!initialSpecialInstructions && !loadState.duplicate_load) {
        setInitialSpecialInstructions(
          enableLtl ? tripLtlInstructions : loadState.trips[0].instructions
        );
        setFieldValue(
          'specialInstructions',
          enableLtl ? tripLtlInstructions : loadState.trips[0].instructions
        );
      }
    }, [loadState.order_id, isImport, loadState.trips[0].instructions, tripLtlInstructions]);

    // TODO: find a way to remove this call from here, estimated mileage is called too many times on the overall c.e.d.i flow
    useEffect(() => {
      // if loaded miles is manual and has data already we don't override

      if (
        enableHideGoogleMapsAndRoutingCalculations ||
        (isManualLoadedMilesPresentForTrip && !enableLtl && entity === EntityType.ORDER) ||
        (!tripLoadedMilesAuto && enableLtl && entity === EntityType.TRIP && tripEstimatedMileage) ||
        isAutoCalculateMileageDisabled ||
        renderCount < 2
      ) {
        setRenderCount(renderCount + 1);
        return;
      }
      const stops =
        enableLtl && entity === EntityType.TRIP
          ? [...provider_trip_stops]
          : [...loadState.provider_stops];

      const stopsLength = stops.length;
      if (stopsLength < MIN_STOP_COUNT) return;

      // For driver pay calculator.

      const firstStop = stops.shift();
      const lastStop = stops.pop();

      const firstLocationID = firstStop?.provider_location?.id;
      const lastLocationID = lastStop?.provider_location?.id;

      if (firstLocationID && lastLocationID) {
        const intermediateStopsIds: string[] = [];

        stops.map((stop) => {
          const intermediateLocationsKey = stop.provider_location?.id;
          if (intermediateLocationsKey) {
            intermediateStopsIds.push(intermediateLocationsKey);
          }
        });

        LoadsService.getEstimatedMilage(firstLocationID, lastLocationID, intermediateStopsIds).then(
          (res) => {
            const source = !enablePcMiler
              ? PROVIDER.GOOGLE
              : routingSettings?.provider ?? PROVIDER.GOOGLE;
            const mileage = Number(parseFloat(res.milage).toFixed(2));

            if (enableLtl && entity === EntityType.TRIP) {
              dispatch(
                updateTripLoadedMiles({
                  value: mileage,
                  is_auto: true,
                  source
                })
              );
            } else {
              dispatch(updateAutomaticMileage({ segmentId: segments[0].ui_id, mileage }));
              dispatch(
                updateEstimatedMileageSegment({
                  segmentId: segments[0].ui_id,
                  value: mileage,
                  is_auto: true,
                  source
                })
              );
            }
          }
        );
      }
    }, [
      JSON.stringify(loadState.provider_stops.map((s: SliceStop) => s.provider_location)),
      JSON.stringify(provider_trip_stops.map((s) => s.provider_location))
    ]);

    useEffect(() => {
      const providerDrivers =
        enableLtl && entity === EntityType.TRIP
          ? [...ltlTripDrivers]
          : [...loadState.trips[0].selected_drivers];
      const newCurrentDrivers: ContactFormDriver[] = providerDrivers.map((driver) => ({
        id: driver.id,
        driver_pay_override: driver.driver_pay_override,
        driver_pay_override_data: driver.driver_pay_override_data,
        send_driver_link: driver.send_driver_link,
        autocompleteDriver: drivers?.find((d) => d.id === driver.id) || null,
        trip_driver_pay_other_payment: driver.trip_driver_pay_other_payment
      }));
      setFieldValue('drivers', newCurrentDrivers);
      setCurrentDrivers(newCurrentDrivers);
    }, [
      JSON.stringify(loadState.trips[0].selected_drivers) || '',
      JSON.stringify(drivers.map((s) => s.id)),
      JSON.stringify(ltlTripDrivers)
    ]);

    useEffect(() => {
      const ownerOperatorToFind =
        enableLtl && entity === EntityType.TRIP
          ? tripSelectedOwnerOperator
          : loadState.trips[0].selected_owner_operator;
      const ownerOperator = ownerOperators.find((o) => o.id === ownerOperatorToFind);
      setFieldValue('ownerOperator', ownerOperator);
      setSelectedOwnerOperator(ownerOperator);
    }, [loadState.trips[0].selected_owner_operator, ownerOperators, tripSelectedOwnerOperator]);

    useEffect(() => {
      const truckToFind =
        enableLtl && entity === EntityType.TRIP
          ? tripSelectedTruck
          : loadState.trips[0].selected_truck;
      const selectedTruck = trucks.find((truck) => truck.id === truckToFind);
      setSelectedTruck(selectedTruck);
    }, [
      loadState.trips[0].selected_truck,
      tripSelectedTruck,
      JSON.stringify(trucks.map((t) => t.id))
    ]);

    useEffect(() => {
      const trailerToFind =
        enableLtl && entity === EntityType.TRIP
          ? tripSelectedTrailer
          : loadState.trips[0].selected_trailer;
      const selectedTrailer = trailers.find((trailer) => trailer.id === trailerToFind);
      setSelectedTrailer(selectedTrailer);
    }, [
      loadState.trips[0].selected_trailer,
      tripSelectedTrailer,
      JSON.stringify(trailers.map((t) => t.id))
    ]);

    useEffect(() => {
      const carrierToFind =
        enableLtl && entity === EntityType.TRIP
          ? tripSelectedCarrier
          : loadState.trips[0].selected_carrier;
      const selectedCarrier = carriers.find((carrier) => carrier.id === carrierToFind);
      setSelectedCarrier(selectedCarrier);
    }, [
      loadState.trips[0].selected_carrier,
      tripSelectedCarrier,
      JSON.stringify(carriers.map((c) => c.id))
    ]);

    useEffect(() => {
      const newCarriers = carriersData.content.map((carrier) => ({
        id: carrier.id,
        label: carrier.name,
        value: carrier.id
      }));

      setCarriers(newCarriers);
    }, [JSON.stringify(carriersData.content)]);

    useEffect(() => {
      const newOwnerOperators = ownerOperatorsList.content.map((ownerOperator: OwnerOperator) => ({
        id: ownerOperator.id,
        label: ownerOperator.name,
        value: ownerOperator.id
      }));
      setOwnerOperators(newOwnerOperators);
    }, [JSON.stringify(ownerOperatorsList.content)]);

    useEffect(() => {
      const newDrivers = driversData.content.map((driver) => {
        return {
          id: driver.id,
          label: `${driver.first_name} ${driver.last_name}`,
          extraLabel: driver.owner_operator ? `(${driver.owner_operator.company.name})` : undefined,
          subLabel:
            driver.phone && driver.email_address
              ? `${StringUtils.formatPhoneNumber(driver.phone)} • ${driver.email_address}`
              : driver.phone
              ? StringUtils.formatPhoneNumber(driver.phone)
              : driver.email_address
              ? driver.email_address
              : '',
          value: driver
        };
      });

      setDrivers(newDrivers);
    }, [JSON.stringify(driversData.content)]);

    useEffect(() => {
      const newEquipments = equipmentData.equipments.map((equipment) => ({
        id: equipment.id,
        label: equipment.name,
        value: equipment
      }));

      setTrailers(
        newEquipments.filter(
          (e) =>
            e.value.type === EquipmentType.trailer &&
            (!(e.value as Equipment).deleted_at ||
              (e.value as Equipment).id === loadState?.trips[0]?.selected_trailer)
        )
      );
      setTrucks(
        newEquipments.filter(
          (e) =>
            e.value.type === EquipmentType.truck &&
            (!(e.value as Equipment).deleted_at ||
              (e.value as Equipment).id === loadState?.trips[0]?.selected_truck)
        )
      );
    }, [JSON.stringify(equipmentData.equipments)]);

    useEffect(() => {
      const newDispatchers = dispatchersData.content.map((dispatcher) => ({
        id: dispatcher.id,
        label: `${dispatcher.first_name} ${dispatcher.last_name}`,
        value: dispatcher
      }));
      setDispatchers(newDispatchers);
    }, [JSON.stringify(dispatchersData.content.map((d) => d.id))]);

    useEffect(() => {
      if (dispatchers.length > 0 && dispatcherEnable) {
        // Edit order or Edit trip to populate dispatcher
        if ((loadState.order_id && !loadState.duplicate_load) || (isEditTripOpen && firstRender)) {
          const dispatcherToUse = isEditTripOpen
            ? tripSelectedDispatcher
            : loadState.selected_dispatcher;
          const selectedDispatcher = dispatchers.find(
            (dispatcher) => dispatcher.id === dispatcherToUse
          );
          setSelectedDispatcher(selectedDispatcher);
          return;
        }

        // Duplicate order
        if (loadState.order_id && loadState.duplicate_load && firstRender) {
          setFirstRender(false);
          assignLoggedDispatcher();
          return;
        }

        // Create order or Create trip
        if ((isCreateLoadOpen || isCreateTripOpen) && firstRender) {
          setFirstRender(false);
          assignLoggedDispatcher();
          return;
        }
      }
    }, [
      loadState.selected_dispatcher,
      loadState.order_id,
      tripSelectedDispatcher,
      dispatchers,
      isEditTripOpen
    ]);

    useEffect(() => {
      if (
        (isEditLoadOpen || isEditTripOpen) &&
        anchorId === 'load-build-drivers-section' &&
        !loading &&
        visibleRef
      ) {
        scrollToView(driverRef, 'center');

        setTimeout(() => dispatch(setAnchorId('')), 1500);
      }
    }, [anchorId, isEditLoadOpen, isEditTripOpen, loading, visibleRef]);

    const disableOwnerOperator = useMemo(() => {
      if (currentDrivers.length > 0) {
        return currentDrivers.some((d) => {
          if (d.autocompleteDriver) {
            const value = (d.autocompleteDriver as TwAutocompleteOption).value as {
              owner_operator?: OwnerOperator;
            };
            return value?.owner_operator;
          } else {
            return false;
          }
        });
      }
      return false;
    }, [currentDrivers]);
    // #endregion

    return (
      <>
        <Box
          ref={ref}
          component="div"
          className={`${
            (isImport && !fullScreenImport) || matchesMobileOrTablet
              ? 'route-info__main-import'
              : 'route-info__main'
          } `}
        >
          <span
            className={
              (isImport && !fullScreenImport) || matchesMobileOrTablet
                ? 'route-info__title-left'
                : 'route-info__title'
            }
          >
            Dispatch
          </span>
          <span
            className={
              isImport || matchesMobileOrTablet
                ? 'route-info__sub-title-left'
                : 'route-info__sub-title'
            }
          >
            Select the parties from the address book or create new ones
          </span>

          <Box sx={dispatchContainerSx}>
            <div id="drivers-container">
              {currentDrivers.map((driver, index) => (
                <Grid container spacing={currentDrivers.length > 1 ? 4 : 2} key={index}>
                  <Grid
                    item
                    xs={12}
                    md={isImport && !fullScreenImport ? 12 : 6}
                    className="tid-contact-details__driver contact-details__driver"
                  >
                    <div
                      className="contact-details__driver-edit"
                      ref={(el: any): void => {
                        driverRef.current = el;
                        setVisibleRef(!!el);
                      }}
                    >
                      <TwAutocomplete
                        name={`driver-${index + 1}`}
                        label={
                          currentDrivers.length > 1 || values.drivers.length > 1
                            ? `Driver ${index + 1}`
                            : 'Driver'
                        }
                        optionList={index === 0 ? driversForOption1 : driversForOption2}
                        renderOption={(props, option): React.ReactNode => {
                          return <RenderOption props={props} option={option} />;
                        }}
                        value={
                          currentDrivers[index].autocompleteDriver ||
                          values.drivers[index].autocompleteDriver ||
                          ''
                        }
                        onChange={(_, newValue: TwAutocompleteOption | null): void => {
                          if (anyDriverPayExists) {
                            if (enableLtl && entity === EntityType.TRIP) {
                              dispatch(
                                updateTripLtlDriverPayConfirmationModal({
                                  open: true,
                                  action: () => handleDriverChange(_, newValue, index, driver)
                                })
                              );
                            } else {
                              dispatch(
                                updateDriverPayConfirmationModal({
                                  open: true,
                                  action: () => handleDriverChange(_, newValue, index, driver)
                                })
                              );
                            }
                          } else {
                            handleDriverChange(_, newValue, index, driver);
                          }
                        }}
                        listAction={{
                          label: 'New driver',
                          action: (): void => {
                            setCurrentDriverIndex(index);
                            setCreateDriverDialogOpen(true);
                          }
                        }}
                        disableNoOption
                        enableClearIcon
                        onKeyDown={onKeyDownHandler}
                      />

                      {!!driver.id && !!driver.autocompleteDriver && (
                        <TwTooltip title={getEditTooltipText('driver')} placement="top">
                          <EditIcon
                            className="contact-details__edit-icon"
                            onClick={(): void => openEditDialog(values, 'driver', index)}
                          />
                        </TwTooltip>
                      )}
                      {currentDrivers.length > 1 && (
                        <CloseIcon
                          className="contact-details__close"
                          onClick={(): void => {
                            if (enableLtl && entity === EntityType.TRIP) {
                              if (anyDriverPayExists) {
                                dispatch(
                                  updateTripLtlDriverPayConfirmationModal({
                                    open: true,
                                    action: () =>
                                      dispatch(deleteTripDriver({ driverOrder: index + 1 }))
                                  })
                                );
                              } else {
                                dispatch(deleteTripDriver({ driverOrder: index + 1 }));
                              }
                            } else {
                              if (anyDriverPayExists) {
                                dispatch(
                                  updateDriverPayConfirmationModal({
                                    open: true,
                                    action: () =>
                                      dispatch(
                                        deleteDriver({ segmentOrder: 1, driverOrder: index + 1 })
                                      )
                                  })
                                );
                              } else {
                                dispatch(deleteDriver({ segmentOrder: 1, driverOrder: index + 1 }));
                              }
                            }
                          }}
                        />
                      )}
                    </div>
                  </Grid>
                  <Grid item xs={12} md={isImport && !fullScreenImport ? 12 : 6}>
                    {advancedDriverFieldsEnable && !!driver.id && enableDriverGrossPayEstimate && (
                      <TwDriverPayCalculator
                        driver={driver}
                        onGrossPayEstimateDataSave={(
                          grossPayEstimateData: TripDriverOverrideData
                        ): void => {
                          if (enableLtl && entity === EntityType.TRIP) {
                            dispatch(
                              updateTripDriverPayData({
                                driverIndex: index,
                                grossPayEstimateData
                              })
                            );
                          } else {
                            dispatch(
                              updateGrossPayEstimateData({
                                driverIndex: index,
                                grossPayEstimateData,
                                segmentOrder: 1
                              })
                            );
                          }
                        }}
                        handleOpenEditDriver={(): void => openEditDialog(values, 'driver', index)}
                        driversNumber={currentDrivers.length}
                        onOtherLoadPayItemsSave={(
                          otherLoadPayItems: TripDriverOtherPay[]
                        ): void => {
                          if (enableLtl && entity === EntityType.TRIP) {
                            dispatch(
                              updateTripOtherPayItemsData({
                                driverIndex: index,
                                otherPayItemsData: otherLoadPayItems
                              })
                            );
                          } else {
                            dispatch(
                              updateOtherPayItemsData({
                                driverIndex: index,
                                otherPayItemsData: otherLoadPayItems,
                                segmentOrder: 1
                              })
                            );
                          }
                        }}
                        entity={entity}
                        tripId={enableLtl ? tripUiId : segments[0].ui_id}
                        ownerOperatorId={selectedOwnerOperator?.id}
                      />
                    )}
                  </Grid>
                </Grid>
              ))}

              {currentDrivers.length < MAXIMUM_DRIVERS_ALLOWED &&
                enableTeamDrivers &&
                currentDrivers.every((d) => !!d.id) && (
                  <Grid container>
                    <Grid
                      item
                      xs={12}
                      onClick={addAnotherDriver}
                      sx={{
                        color: '#3A4DE9',
                        fontSize: '14px',
                        cursor: 'pointer',
                        paddingTop: '16px',
                        marginBottom: '16px'
                      }}
                    >
                      + Add another driver
                    </Grid>
                  </Grid>
                )}
            </div>
            {ownerOperatorEnabled && (
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={isImport && !fullScreenImport ? 12 : 6}
                  className="contact-details__owner-operator-edit"
                >
                  <TwAutocomplete
                    id="ownerOperator"
                    name="ownerOperator"
                    label="Owner Operator"
                    optionList={ownerOperators}
                    value={selectedOwnerOperator || values.ownerOperator || ''}
                    onChange={(_, newValue: TwAutocompleteOption | null): void => {
                      if (enableLtl && entity === EntityType.TRIP) {
                        dispatch(updateTripSelectedOwnerOperator({ value: newValue?.id }));
                      } else {
                        dispatch(
                          updateSelectedOwnerOperator({
                            segmentOrder: 1,
                            value: newValue?.id
                          })
                        );
                      }

                      setFieldValue('ownerOperator', newValue);
                    }}
                    disabled={disableOwnerOperator}
                    listAction={{
                      label: 'New owner operator',
                      action: (): void =>
                        setCreateCompanyDialogOpen({
                          entity: CompaniesEntities.ownerOperator,
                          open: true
                        })
                    }}
                    disableNoOption
                    enableClearIcon
                    onKeyDown={onKeyDownHandler}
                  />
                </Grid>
              </Grid>
            )}
            <Grid container spacing={2}>
              {carriersEnabled && (
                <Grid
                  item
                  xs={12}
                  md={isImport && !fullScreenImport ? 12 : 6}
                  className="contact-details__carrier-edit"
                >
                  <TwAutocomplete
                    id="carrier"
                    name="carrier"
                    label="Carrier"
                    optionList={carriers}
                    value={selectedCarrier || values.carrier || ''}
                    onChange={(_, newValue: TwAutocompleteOption | null): void => {
                      if (enableLtl && entity === EntityType.TRIP) {
                        dispatch(updateTripSelectedCarrier({ value: newValue?.id }));
                      } else {
                        dispatch(
                          updateSelectedCarrier({
                            segmentOrder: 1,
                            value: newValue?.id
                          })
                        );
                      }

                      setFieldValue('carrier', newValue);
                    }}
                    listAction={{
                      label: 'New carrier',
                      action: (): void =>
                        setCreateCompanyDialogOpen({
                          entity: CompaniesEntities.carrier,
                          open: true
                        })
                    }}
                    disableNoOption
                    enableClearIcon
                    onKeyDown={onKeyDownHandler}
                  />
                  {(selectedCarrier || values.carrier) && (
                    <TwTooltip title={getEditTooltipText('carrier')} placement="top">
                      <EditIcon
                        className="contact-details__edit-icon"
                        onClick={(): void => openEditDialog(values, 'carrier')}
                      />
                    </TwTooltip>
                  )}
                </Grid>
              )}
              {dispatcherEnable && (
                <Grid
                  item
                  xs={12}
                  md={isImport && !fullScreenImport ? 12 : 6}
                  className="contact-details__dispatcher-edit"
                >
                  <TwAutocomplete
                    id="dispatcher"
                    name="dispatcher"
                    label="Dispatcher"
                    optionList={dispatchers}
                    value={selectedDispatcher ? selectedDispatcher : null}
                    onChange={(_, newValue: TwAutocompleteOption | null): void => {
                      if (enableLtl && entity === EntityType.TRIP) {
                        dispatch(updateTripSelectedDispatcher(newValue?.id));
                      } else {
                        dispatch(updateSelectedDispatcher(newValue?.id));
                      }
                      setSelectedDispatcher(newValue);
                      setFieldValue('dispatcher', newValue);
                    }}
                    listAction={{
                      label: 'New dispatcher',
                      action: (): void => setCreateDispatcherDialogOpen(true)
                    }}
                    disableNoOption={true}
                    enableClearIcon
                    onKeyDown={onKeyDownHandler}
                  />
                  {(selectedDispatcher || values.dispatcher) && (
                    <TwTooltip title={getEditTooltipText('dispatcher')} placement="top">
                      <EditIcon
                        className="contact-details__edit-icon"
                        onClick={(): void => openEditDialog(values, 'dispatcher')}
                      />
                    </TwTooltip>
                  )}
                </Grid>
              )}
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6} className="contact-details__truck-edit">
                <TwEquipmentSelector
                  label="Truck"
                  isEquipmentPreselected={false}
                  selectedEquipment={selectedTruck || values.truck || null}
                  hasError={false}
                  onChange={(equipment: TwAutocompleteOption | null): void => {
                    if (enableLtl && entity === EntityType.TRIP) {
                      dispatch(
                        updateTripSelectedTruck({
                          value: equipment?.id,
                          preserveEmptyMiles: isAutoCalculateMileageDisabled
                        })
                      );
                    } else {
                      dispatch(
                        updateSelectedTruck({
                          segmentOrder: 1,
                          value: equipment?.id,
                          preserveEmptyMiles: isAutoCalculateMileageDisabled
                        })
                      );
                    }

                    setFieldValue('truck', equipment);
                  }}
                  setSelectedEquipment={setSelectedTruck}
                  sx={{ width: '100%' }}
                  equipmentType={EquipmentType.truck}
                />
                {(selectedTruck || values.truck) &&
                  !(selectedTruck?.value as Equipment)?.deleted_at && (
                    <TwTooltip title={getEditTooltipText('truck')} placement="top">
                      <EditIcon
                        className="contact-details__edit-icon"
                        onClick={(): void => openEditDialog(values, 'truck')}
                      />
                    </TwTooltip>
                  )}
              </Grid>
              <Grid item xs={6} className="contact-details__trailer-edit">
                <TwEquipmentSelector
                  label="Trailer"
                  isEquipmentPreselected={false}
                  selectedEquipment={selectedTrailer || values.trailer || null}
                  hasError={false}
                  onChange={(equipment: TwAutocompleteOption | null): void => {
                    if (enableLtl && entity === EntityType.TRIP) {
                      dispatch(updateTripSelectedTrailer({ value: equipment?.id }));
                    } else {
                      dispatch(
                        updateSelectedTrailer({
                          segmentOrder: 1,
                          value: equipment?.id
                        })
                      );
                    }

                    setFieldValue('trailer', equipment);
                  }}
                  setSelectedEquipment={setSelectedTrailer}
                  sx={{ width: '100%' }}
                  equipmentType={EquipmentType.trailer}
                />
                {(selectedTrailer || values.trailer) &&
                  !(selectedTrailer?.value as Equipment)?.deleted_at && (
                    <TwTooltip title={getEditTooltipText('trailer')} placement="top">
                      <EditIcon
                        className="contact-details__edit-icon"
                        onClick={(): void => openEditDialog(values, 'trailer')}
                      />
                    </TwTooltip>
                  )}
              </Grid>
            </Grid>

            {!enableHideGoogleMapsAndRoutingCalculations && (
              <Stack flexDirection={'row'} gap={2}>
                {isEmptyMilesEnabled && (
                  <TwEmptyMiles
                    firstStop={
                      enableLtl && entity === EntityType.TRIP
                        ? provider_trip_stops[0]
                        : segments[0].provider_stops[0] || null
                    }
                    truck={selectedTruck || null}
                    initialEmptyMiles={
                      isImport
                        ? null
                        : enableLtl && entity === EntityType.TRIP
                        ? tripEmptyMiles
                        : segments[0].empty_miles
                    }
                    initialAutoCalculate={
                      enableLtl && entity === EntityType.TRIP
                        ? tripIsEmptyMilesAuto
                        : segments[0].is_empty_miles_auto
                    }
                    initialSource={
                      enableLtl && entity === EntityType.TRIP
                        ? tripEmptyMileageSource || null
                        : segments[0].empty_mileage_source || null
                    }
                    segmentId={enableLtl && entity === EntityType.TRIP ? tripId : segments[0].id}
                    segmentUid={
                      enableLtl && entity === EntityType.TRIP ? tripUiId : segments[0].ui_id
                    }
                    entity={entity}
                  />
                )}
                <TwLoadedMiles
                  stops={
                    enableLtl && entity === EntityType.TRIP
                      ? provider_trip_stops
                      : segments[0].provider_stops
                  }
                  segmentId={enableLtl && entity === EntityType.TRIP ? tripUiId : segments[0].ui_id}
                  initialLoadedMiles={
                    enableLtl && entity === EntityType.TRIP
                      ? tripEstimatedMileage
                      : segments[0].estimated_mileage
                  }
                  initialAutoCalculate={
                    enableLtl && entity === EntityType.TRIP
                      ? tripLoadedMilesAuto
                      : segments[0].is_loaded_miles_auto
                  }
                  initialSource={
                    enableLtl && entity === EntityType.TRIP
                      ? tripLoadedMileageSource || null
                      : segments[0].loaded_mileage_source || null
                  }
                  entity={entity}
                />
              </Stack>
            )}

            <TwExpandableTextarea
              id="specialInstructions"
              name="specialInstructions"
              label="Trip notes"
              maxRows={5}
              value={specialInstructions || initialSpecialInstructions || ''}
              onInputBlur={(e): void => {
                if (enableLtl && entity === EntityType.TRIP) {
                  dispatch(updateTripNotes(e.target.value));
                } else {
                  dispatch(
                    updateTripInstructions({ segmentOrder: 1, instructions: e.target.value })
                  );
                }
              }}
              onInputChange={(e): void => {
                if (initialSpecialInstructions) setInitialSpecialInstructions(undefined);
                setSpecialInstructions(e.target.value);
              }}
            />
          </Box>
        </Box>
        {createCompanyDialogOpen.open && (
          <TwCompaniesModal
            open={createCompanyDialogOpen.open}
            onClose={(): void => {
              setCreateCompanyDialogOpen({
                entity: CompaniesEntities.carrier,
                open: false
              });
            }}
            entity={createCompanyDialogOpen.entity}
            onSuccess={(res): void => onCompanyCreated(res)}
          />
        )}

        <TwEditCompaniesModal
          open={editCarrierOpen}
          onClose={handleEditCompaniesClose}
          entity={CompaniesEntities.carrier}
          entityId={selectedCarrierId || ''}
        />

        <CreateDriver
          open={createDriverDialogOpen}
          onClose={(): void => setCreateDriverDialogOpen(false)}
          onSuccess={onDriverCreated}
          onCarrierCreated={(newCarrier): void => {
            if (enableLtl && entity === EntityType.TRIP) {
              dispatch(
                addTripCarrier({
                  carrierId: newCarrier.id,
                  abortUpdate: true
                })
              );
            } else {
              dispatch(
                addCarrier({
                  carrierId: newCarrier.id,
                  segmentOrder: 1,
                  abortUpdate: true
                })
              );
            }
          }}
          onEquipmentCreated={(newEquipment): void => {
            if (enableLtl && entity === EntityType.TRIP) {
              dispatch(
                addTripEquipment({
                  equipment: {
                    id: newEquipment.id,
                    type: newEquipment.type
                  },
                  abortUpdate: true
                })
              );
            } else {
              dispatch(
                addEquipment({
                  equipment: {
                    id: newEquipment.id,
                    type: newEquipment.type
                  },
                  segmentOrder: 1,
                  abortUpdate: true
                })
              );
            }
          }}
        />

        <CreateEquipment
          open={createEquipmentDialogOpen}
          defaultType={createEquipmentType}
          onClose={(): void => setCreateEquipmentDialogOpen(false)}
          onSuccess={onEquipmentCreated}
        />
        <CreateDispatcher
          open={createDispatcherDialogOpen}
          onClose={(): void => setCreateDispatcherDialogOpen(false)}
          onSuccess={onDispatcherCreated}
        />

        <EditDriver
          driverId={selectedDriverId}
          open={editDriverOpen}
          onClose={handleEditDriverClose}
          onSuccess={onDriverEditSuccess}
          onCarrierCreated={(newCarrier): void => {
            if (enableLtl && entity === EntityType.TRIP) {
              dispatch(
                addTripCarrier({
                  carrierId: newCarrier.id,
                  abortUpdate: true
                })
              );
            } else {
              dispatch(
                addCarrier({
                  carrierId: newCarrier.id,
                  segmentOrder: 1,
                  abortUpdate: true
                })
              );
            }
          }}
          onEquipmentCreated={(newEquipment): void => {
            if (enableLtl && entity === EntityType.TRIP) {
              dispatch(
                addTripEquipment({
                  equipment: {
                    id: newEquipment.id,
                    type: newEquipment.type
                  },
                  abortUpdate: true
                })
              );
            } else {
              dispatch(
                addEquipment({
                  equipment: {
                    id: newEquipment.id,
                    type: newEquipment.type
                  },
                  segmentOrder: 1,
                  abortUpdate: true
                })
              );
            }
          }}
          entity={entity}
        />

        <EditEquipment
          equipmentId={selectedEquipmentId}
          open={editEquipmentOpen}
          onClose={handleEditEquipmentClose}
          // onSuccess={handleEditEquipmentSuccess}
        />

        <EditDispatcher
          dispatcherId={selectedDispatcherId}
          open={editDispatcherOpen}
          onClose={handleEditDispatcherClose}
        />
      </>
    );
  }
);
DispatchInfo.displayName = 'DispatchInfo';
export default DispatchInfo;
